import axios, { AxiosInstance, AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { ErrorService } from './ErrorService';
import { AppError } from '../types/errors';

/**
 * Interface for error handler function
 */
type ErrorHandler = (error: AppError) => void;

/**
 * HTTP interceptor service for handling API errors automatically
 */
export class HttpInterceptorService {
  private static errorHandler: ErrorHandler | null = null;
  private static isInterceptorSetup = false;

  /**
   * Setup axios interceptors with error handling
   */
  static setupInterceptors(
    axiosInstance: AxiosInstance = axios,
    errorHandler?: ErrorHandler
  ): void {
    if (this.isInterceptorSetup) {
      return;
    }

    // Store the error handler
    if (errorHandler) {
      this.errorHandler = errorHandler;
    }

    // Request interceptor
    axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Add any common headers or request modifications here
        // For example, adding auth tokens, timestamps, etc.
        return config;
      },
      (error: AxiosError) => {
        // Handle request errors
        this.handleError(error, 'Request failed');
        return Promise.reject(error);
      }
    );

    // Response interceptor
    axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        // Handle successful responses
        return response;
      },
      (error: AxiosError) => {
        // Handle response errors
        this.handleError(error, 'API request failed');
        return Promise.reject(error);
      }
    );

    this.isInterceptorSetup = true;
  }

  /**
   * Handle errors from axios interceptors
   */
  private static handleError(error: AxiosError, defaultMessage: string): void {
    try {
      // Process the error using ErrorService
      const processedError = ErrorService.processError(error, {
        source: 'HTTP_INTERCEPTOR',
        url: error.config?.url,
        method: error.config?.method,
        defaultMessage,
      });

      // Call the error handler if available
      if (this.errorHandler) {
        this.errorHandler(processedError);
      } else {
        // Fallback: log to console if no error handler is set
        console.error('HTTP Error:', processedError);
      }
    } catch (handlerError) {
      // Prevent infinite loops if error handling itself fails
      console.error('Error in error handler:', handlerError);
      console.error('Original error:', error);
    }
  }

  /**
   * Set the global error handler
   */
  static setErrorHandler(handler: ErrorHandler): void {
    this.errorHandler = handler;
  }

  /**
   * Remove the error handler
   */
  static removeErrorHandler(): void {
    this.errorHandler = null;
  }

  /**
   * Check if interceptors are already setup
   */
  static isSetup(): boolean {
    return this.isInterceptorSetup;
  }

  /**
   * Reset the interceptor setup state (useful for testing)
   */
  static reset(): void {
    this.isInterceptorSetup = false;
    this.errorHandler = null;
  }

  /**
   * Create a new axios instance with error handling pre-configured
   */
  static createAxiosInstance(baseURL?: string, errorHandler?: ErrorHandler): AxiosInstance {
    const instance = axios.create({
      baseURL,
      timeout: 30000, // 30 second timeout
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Setup interceptors for this instance
    const tempSetupState = this.isInterceptorSetup;
    this.isInterceptorSetup = false; // Allow setup for new instance
    this.setupInterceptors(instance, errorHandler);
    this.isInterceptorSetup = tempSetupState; // Restore original state

    return instance;
  }

  /**
   * Wrapper for making HTTP requests with automatic error handling
   */
  static async request<T = unknown>(
    config: InternalAxiosRequestConfig,
    customErrorHandler?: ErrorHandler
  ): Promise<T> {
    try {
      const response = await axios.request<T>(config);
      return response.data;
    } catch (error) {
      // Handle the error
      const processedError = ErrorService.processError(error, {
        source: 'HTTP_REQUEST',
        url: config.url,
        method: config.method,
      });

      // Use custom handler if provided, otherwise use global handler
      const handler = customErrorHandler || this.errorHandler;
      if (handler) {
        handler(processedError);
      }

      // Re-throw the error so the calling code can handle it if needed
      throw processedError;
    }
  }

  /**
   * Convenience methods for common HTTP verbs
   */
  static async get<T = unknown>(url: string, config?: InternalAxiosRequestConfig): Promise<T> {
    return this.request<T>({
      ...config,
      method: 'GET',
      url,
      headers: config?.headers || {},
    } as InternalAxiosRequestConfig);
  }

  static async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: InternalAxiosRequestConfig
  ): Promise<T> {
    return this.request<T>({
      ...config,
      method: 'POST',
      url,
      data,
      headers: config?.headers || {},
    } as InternalAxiosRequestConfig);
  }

  static async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: InternalAxiosRequestConfig
  ): Promise<T> {
    return this.request<T>({
      ...config,
      method: 'PUT',
      url,
      data,
      headers: config?.headers || {},
    } as InternalAxiosRequestConfig);
  }

  static async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: InternalAxiosRequestConfig
  ): Promise<T> {
    return this.request<T>({
      ...config,
      method: 'PATCH',
      url,
      data,
      headers: config?.headers || {},
    } as InternalAxiosRequestConfig);
  }

  static async delete<T = unknown>(url: string, config?: InternalAxiosRequestConfig): Promise<T> {
    return this.request<T>({
      ...config,
      method: 'DELETE',
      url,
      headers: config?.headers || {},
    } as InternalAxiosRequestConfig);
  }
}

// Export a pre-configured axios instance
export const apiClient = HttpInterceptorService.createAxiosInstance();

// Export the service as default
export default HttpInterceptorService;
